import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../layouts/Navbar";
import Footer from "../../layouts/Footer";
import Sidebar from "../../layouts/Sidebar";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CreateCategory = () => {
  const [formData, setFormData] = useState({ 
    title: "", 
    year: new Date().getFullYear().toString() 
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // Generate year options from 2000 to current year + 1
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2000 + 2 }, // +2 to include next year
    (_, i) => (currentYear + 1 - i).toString()
  );

  // Fetch existing categories
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://pastq.eddcodesgh.com/qApi/admin/categories/_viewcategories.php",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );
      
      if (response.data.status === 'success') {
        setCategories(response.data.records || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Category title is required";
      toast.error("Category title is required", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    if (!formData.year) {
      newErrors.year = "Year is required";
      toast.error("Please select a year", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.post(
        "https://pastq.eddcodesgh.com/qApi/admin/categories/_addcategory.php",
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 200) {
        toast.success("Category created successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setFormData({ title: "", year: currentYear.toString() });
        // Refresh categories list
        fetchCategories();
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while creating category", {
        position: "top-right",
        autoClose: 4000,
      });
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "An error occurred",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />
     
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Create New Category</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/categories">Categories</Link>
              </li>
              <li className="breadcrumb-item active">Create New</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="row">
            {/* Left Column - Create Form */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Create New Category</h5>
                  <p className="text-muted mb-4">
                    Add a new category with title and year for better organization of past questions.
                  </p>

                  <form onSubmit={handleSubmit}>
                    {errors.submit && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.submit}
                        <button type="button" className="btn-close" onClick={() => setErrors({})}></button>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-8">
                        <label htmlFor="title" className="form-label">
                          Category Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className={`form-control ${errors.title ? "is-invalid" : ""}`}
                          placeholder="e.g., BECE, WASSCE, SSSCE, NABTEX"
                          value={formData.title}
                          onChange={handleChange}
                          required
                        />
                        {errors.title && (
                          <div className="invalid-feedback">{errors.title}</div>
                        )}
                        <div className="form-text">
                          Enter a descriptive title for the category
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <label htmlFor="year" className="form-label">
                          Year <span className="text-danger">*</span>
                        </label>
                        <select
                          id="year"
                          name="year"
                          className={`form-select ${errors.year ? "is-invalid" : ""}`}
                          value={formData.year}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Year</option>
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                        {errors.year && (
                          <div className="invalid-feedback">{errors.year}</div>
                        )}
                        <div className="form-text">
                          Select the academic year
                        </div>
                      </div>
                    </div>

                    <div className="text-center mt-4">
                      <button
                        className="btn btn-primary btn-lg px-5"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-plus-circle me-2"></i>
                            Create Category
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Column - Recent Categories */}
            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Recent Categories</h5>
                    <Link to="/admin/categories" className="btn btn-sm btn-outline-primary">
                      View All
                    </Link>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : categories.length > 0 ? (
                    <div className="list-group">
                      {categories.slice(0, 5).map((category) => (
                        <div
                          key={category.id}
                          className="list-group-item list-group-item-action border-0 py-3"
                        >
                          <div className="d-flex w-100 justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">{category.cat_title}</h6>
                              <small className="text-muted">
                                <i className="bi bi-calendar me-1"></i>
                                Year: {category.year || "N/A"}
                              </small>
                            </div>
                            <span className="badge bg-primary rounded-pill">
                              ID: {category.id}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="bi bi-inbox text-muted display-6 mb-3"></i>
                      <p className="text-muted mb-0">No categories found</p>
                      <small>Create your first category above</small>
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-top">
                    <h6 className="text-muted mb-2">Quick Stats</h6>
                    <div className="row text-center">
                      <div className="col-6">
                        <div className="p-3 bg-light rounded-3">
                          <div className="text-primary fw-bold h4 mb-1">{categories.length}</div>
                          <small className="text-muted">Total Categories</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-3 bg-light rounded-3">
                          <div className="text-success fw-bold h4 mb-1">
                            {new Set(categories.map(c => c.year)).size}
                          </div>
                          <small className="text-muted">Years Covered</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Card */}
              <div className="card mt-3 border-primary">
                <div className="card-body">
                  <h6 className="card-title text-primary">
                    <i className="bi bi-info-circle me-2"></i>
                    Category Guidelines
                  </h6>
                  <ul className="list-unstyled mb-0 small">
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Use clear, descriptive titles
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Select appropriate academic year
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Avoid duplicate category names
                    </li>
                    <li>
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Categories organize past questions
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style jsx>{`
        .card {
          border-radius: 15px;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        
        .card-title {
          color: #2c3e50;
          font-weight: 600;
        }
        
        .form-label {
          font-weight: 500;
          color: #495057;
        }
        
        .list-group-item {
          border-radius: 10px;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }
        
        .list-group-item:hover {
          background-color: rgba(13, 110, 253, 0.05);
          transform: translateX(5px);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 12px 30px;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
        }
        
        .breadcrumb {
          background: transparent;
          padding: 0;
          margin-bottom: 1rem;
        }
        
        .breadcrumb-item a {
          color: #667eea;
          text-decoration: none;
        }
        
        .breadcrumb-item.active {
          color: #6c757d;
        }
        
        .pagetitle h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }
        
        .text-muted {
          color: #6c757d !important;
        }
      `}</style>
    </>
  );
};

export default CreateCategory;